import React, { PropsWithChildren } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { PortalWithState } from 'react-portal';
import { DropdownIndicatorProps } from 'react-select';
import CustomerEditModal from './CustomerEditModal';
import ReactSelectButton from '../ReactSelect/ReactSelectButton';
import { getNameLikeCondition } from '../../helpers/search';
import { gql } from '@/modes/individual/gql';

export const FETCH_CUSTOMERS = gql(`
  query GetCustomers(
    $where: JSON
    $profileWhere: JSON
    $profileRequired: Boolean
    $serviceAddressWhere: JSON
    $serviceAddressesRequired: Boolean
    $orderBy: [ColumnOrder]
  ) {
    customers(where: $where, limit: 100, orderBy: $orderBy) {
      id
      profileId
      profile(where: $profileWhere, required: $profileRequired) {
        id
        name
      }
      accountNumber
      serviceAddresses(
        where: $serviceAddressWhere
        required: $serviceAddressesRequired
        limit: null
      ) {
        id
        addressId
      }
    }
  }
`);

export type Customer = {
  id: string;
  profileId: string;
  profile: {
    id: string;
    name: string;
  };
  accountNumber?: string;
};

type CustomerPickerProps = {
  customerProfileId?: string | null;
  serviceAddressWhere?: any;
  waterServiceId?: string;
  serviceAddressId?: string;
  where?: any;
  onChange: (address: Customer | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  allowCreate?: boolean;
  isDisabled?: boolean;
};

const CustomerPicker = ({
  customerProfileId,
  where,
  onChange,
  serviceAddressWhere,
  waterServiceId,
  serviceAddressId,
  placeholder,
  isClearable,
  allowCreate,
  isDisabled,
}: CustomerPickerProps): JSX.Element => {
  where = where ?? {};
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery<{
    customers: Customer[];
  }>(FETCH_CUSTOMERS, {
    variables: {
      where: {
        profileId: customerProfileId,
      },
    },
    skip: !customerProfileId,
  });

  const selected: Customer | undefined = itemData?.customers[0];

  if (itemError) {
    throw itemError;
  }

  const effectiveServiceAddressWhere =
    serviceAddressWhere || waterServiceId || serviceAddressId
      ? {
          ...serviceAddressWhere,
          ...(waterServiceId ? { waterServiceId } : {}),
          ...(serviceAddressId ? { addressId: serviceAddressId } : {}),
        }
      : undefined;

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query<{ customers: Customer[] }>({
      query: FETCH_CUSTOMERS,
      fetchPolicy: 'network-only',
      variables: {
        serviceAddressesRequired: !inputValue && !!effectiveServiceAddressWhere,
        serviceAddressWhere: inputValue
          ? undefined
          : effectiveServiceAddressWhere,
        profileWhere: inputValue
          ? {
              name: getNameLikeCondition(inputValue),
            }
          : undefined,
        profileRequired: !!inputValue && !!effectiveServiceAddressWhere,
        where,
        limit: null,
        orderBy: inputValue ? undefined : [{ col: 'profile.name', dir: 'ASC' }],
      },
    });

    if (res.data?.customers) {
      if (!selected && res.data.customers.length === 1) {
        onChange(res.data.customers[0]);
      }
      return res.data.customers;
    }

    return [];
  };

  return (
    <PortalWithState closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => {
        return (
          <>
            <AsyncSelect
              key={JSON.stringify(effectiveServiceAddressWhere)}
              cacheOptions
              isMulti={false}
              components={{
                DropdownIndicator: (
                  props: PropsWithChildren<
                    DropdownIndicatorProps<Customer, false>
                  >
                ) => {
                  return allowCreate && !isDisabled ? (
                    <ReactSelectButton
                      onClick={openPortal}
                      title="Add new customer"
                    />
                  ) : null;
                },
              }}
              value={selected}
              loadOptions={loadOptionsFunc}
              defaultOptions
              isDisabled={isDisabled}
              isClearable={isClearable}
              noOptionsMessage={() => null}
              menuPlacement="auto"
              onChange={(selected: Customer | null) => {
                if (!selected) {
                  onChange(null);
                } else {
                  onChange(selected);
                }
              }}
              placeholder={placeholder ?? 'Enter customer name...'}
              getOptionLabel={(customer) =>
                customer.profile.name +
                (customer.accountNumber ? ` (${customer.accountNumber})` : '')
              }
              getOptionValue={(customer) => customer.profileId}
            />
            {portal(
              <CustomerEditModal
                action="add"
                waterServiceId={waterServiceId}
                serviceAddressId={serviceAddressId}
                customer={{
                  profile: {
                    contactAddressId: serviceAddressId,
                  },
                }}
                onSave={onChange}
                onClose={closePortal}
              />
            )}
          </>
        );
      }}
    </PortalWithState>
  );
};

export default CustomerPicker;
